import { useMutation, useQuery } from '@apollo/client';
import React from 'react';
import { useParams } from 'react-router';
import type { WebhookValues } from './AdminWebhookForm';
import AdminWebhookForm from './AdminWebhookForm';
import { ALL_WEBHOOKS_QUERY } from './AdminWebhooksView';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import ConfirmationModal from '../../components/ConfirmationModal';
import { gql } from '../../graphql/generated/gql';
import { UIActions } from '../../ui/uiSlice';
import { legacyUpdateQueryList } from '../../utils/graphql/updateQueryList';
import useNavigate from '../../utils/router/useNavigate';

const ADMIN_WEBHOOK_QUERY = gql(/** @complexity 3 */ `
  query AdminWebhookQuery($id: ID!) {
    webhook(id: $id) {
      id
      ...AdminWebhookForm_webhook
    }
  }
`);

const UPDATE_WEBHOOK_MUTATION = gql(/** @complexity 6 */ `
  mutation updateWebhook(
    $id: ID!
    $enableAllPosts: Boolean
    $events: [WebhookEvent!]!
    $name: String!
    $targetUrl: String!
    $topics: [ID!]
  ) {
    updateWebhook(
      id: $id
      enableAllPosts: $enableAllPosts
      events: $events
      name: $name
      targetUrl: $targetUrl
      topics: $topics
    ) {
      id
      ...AdminWebhookForm_webhook
    }
  }
`);

const REGENERATE_WEBHOOK_SECRET_MUTATION = gql(/** @complexity 2 */ `
  mutation regenerateWebhookSecret($id: ID!) {
    regenerateWebhookSecret(id: $id) {
      id
      secret
    }
  }
`);

const DELETE_WEBHOOK_MUTATION = gql(/** @complexity 2 */ `
  mutation deleteWebhook($id: ID!) {
    deleteWebhook(id: $id) {
      id
    }
  }
`);

const AdminWebhookEditView = () => {
  const navigate = useNavigate();
  const { id } = useParams() as { id: string };

  const dispatch = useAppDispatch();

  const { data } = useQuery(ADMIN_WEBHOOK_QUERY, {
    variables: { id },
  });
  const [updateWebhook] = useMutation(UPDATE_WEBHOOK_MUTATION);
  const [regenerateSecret] = useMutation(REGENERATE_WEBHOOK_SECRET_MUTATION);
  const [deleteWebhook] = useMutation(DELETE_WEBHOOK_MUTATION);

  const handleSubmit = async (values: WebhookValues) => {
    await updateWebhook({ variables: { ...values, id } });
    dispatch(
      UIActions.pushToast({
        level: 'success',
        message: 'Webhook successfully updated',
      }),
    );
  };

  const handleRegenerateSecret = async () => {
    await regenerateSecret({ variables: { id } });
  };

  const handleDelete = () => {
    dispatch(
      UIActions.openModal(ConfirmationModal, {
        title: 'Remove this webhook?',
        confirmButtonText: 'Delete',
        onConfirm: async () => {
          await deleteWebhook({
            variables: { id },
            update: legacyUpdateQueryList(
              { query: ALL_WEBHOOKS_QUERY },
              'session.organization.webhooks',
              'deleteWebhook',
              false,
            ),
          });
          navigate('/admin/developer');
        },
      }),
    );
  };

  if (!data) {
    return null;
  }

  return (
    <AdminWebhookForm
      onSubmit={handleSubmit}
      webhook={data.webhook}
      onDelete={handleDelete}
      onRegenerateSecret={handleRegenerateSecret}
    />
  );
};

export default AdminWebhookEditView;
